import type { InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { FIELD_CONTROL, FIELD_ERROR, FIELD_HINT, FIELD_LABEL, FIELD_MONO } from './field-styles';

/**
 * The controls the customers dialogs are built from.
 *
 * See field-styles.ts for why these do not come from `src/shared/ui`.
 */

/**
 * A labelled control, with its hint or error underneath.
 *
 * `wide` spans both columns of the two-column grid these sit in - the design
 * gives a whole row to the fields whose values do not fit half of one.
 */
export function Field({
  label,
  htmlFor,
  error,
  hint,
  wide = false,
  children,
}: {
  label: string;
  htmlFor?: string;
  error?: string;
  /** Suppressed while an error is showing: two lines of grey and red under
   *  one field is noise at the moment the field needs to be read. */
  hint?: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cn('min-w-0', wide && 'sm:col-span-2')}>
      {/* A plain label rather than the shared one, which carries `text-sm` -
          the point of this treatment is that the design says 11.5px. */}
      <label htmlFor={htmlFor} className={FIELD_LABEL}>
        {label}
      </label>
      {children}
      {hint && !error && <p className={FIELD_HINT}>{hint}</p>}
      {error && <p className={FIELD_ERROR}>{error}</p>}
    </div>
  );
}

export function FieldInput({
  mono = false,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { mono?: boolean }) {
  return <input className={cn(FIELD_CONTROL, mono && FIELD_MONO, className)} {...props} />;
}

/**
 * One of a pair of choice cards - org type, licence mode.
 *
 * 56px tall with the label above a 10.5px hint, and selection shown by the
 * border going to the brand blue over a tinted ground rather than by a
 * checkmark: at this size a control glyph costs more room than it earns.
 */
export function OptionCard({
  title,
  subtitle,
  selected,
  onSelect,
}: {
  title: string;
  subtitle: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'h-14 min-w-0 rounded-[9px] border-[1.5px] px-3.25 text-left transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
        selected ? 'border-primary bg-muted' : 'border-input bg-background hover:bg-accent',
      )}
    >
      <span
        className={cn(
          'block truncate text-[12.5px] font-semibold',
          selected ? 'text-foreground' : 'text-neutral-700 dark:text-neutral-300',
        )}
      >
        {title}
      </span>
      <span className="mt-0.5 block text-[10.5px] text-neutral-400">{subtitle}</span>
    </button>
  );
}
